import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TabsGroup, NavTabsList, NavTabTrigger, TabsContent } from 'ui/tabsGroup';
import { spacing } from 'ui/theme';
import { Table } from 'ui/table';
import TableHeader from 'ui/tableHeader/TableHeader';
import TableRows from 'ui/tableRows/TableRows';
import { sortRows } from 'ui/table/utils';
import { DESCENDING } from 'constants/sortDirections';
import styled from 'styled-components';
import get from 'lodash/get';
import { ActiveMenuItemEnum } from '../types';
import {
  updateActiveDataViewItem,
  updateChartType,
  updateReportDetailsTableConfig,
} from '../actions';
import { getActiveReport, selectReportTableState, selectSummaryViewData } from '../selectors';

const defaultTableConfig = {
  page: 1,
  pageSize: 10,
  sortDesc: true,
  filter: '',
};

const StyledContainer = styled.div`
  margin-top: ${spacing.threeQuarter};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  overflow-x: auto;
`;

const ReportDetails = () => {
  const dispatch = useDispatch();

  const report = useSelector(getActiveReport);
  const dateTimeTableData = useSelector(selectReportTableState);
  const entityViewSummaryData = useSelector(selectSummaryViewData);
  const [activeMenuItem, setActiveMenuItem] = useState(ActiveMenuItemEnum.componentTable);
  const [sortedBy, setSortedBy] = useState('id');
  const [sortDirection, setSortDirection] = useState(DESCENDING);

  const selectActiveMenuItem = (menuItem /*: ActiveMenuItemEnum */) => {
    dispatch(updateActiveDataViewItem(menuItem));
    dispatch(
      updateChartType(
        report.id,
        menuItem,
        menuItem === ActiveMenuItemEnum.componentTable
          ? get(report, 'settings.entityView.chartType', 'bar')
          : get(report, 'settings.dateTimeView.chartType', 'line'),
      ),
    );
    dispatch(updateReportDetailsTableConfig(defaultTableConfig));
    setActiveMenuItem(menuItem);
  };

  const sortedDateTimeRows = sortRows(dateTimeTableData.data, sortedBy, sortDirection);

  const isMetricSummable = (metric) => {
    if (metric === 'id' || metric === 'name') return false;
    const lowerCased = metric.toLowerCase();
    if (
      lowerCased.includes('rate') ||
      lowerCased.includes('unique') ||
      lowerCased.includes('per')
    ) {
      return false;
    }
    return true;
  };

  const sortedSummaryRows = sortRows(entityViewSummaryData.data, sortedBy, sortDirection);
  const entityViewTotal = {};
  const summaryViewColumns = entityViewSummaryData.columns
    ?.map((column) => column.value)
    .filter(isMetricSummable);

  summaryViewColumns?.forEach((column) => {
    sortedSummaryRows?.forEach((row) => {
      if (!entityViewTotal[column]) {
        entityViewTotal[column] = row[column];
      } else {
        entityViewTotal[column] += row[column];
      }
    });
  });

  let sortedSummaryRowsWithTotal = [...sortedSummaryRows];
  if (Object.keys(entityViewTotal).length) {
    sortedSummaryRowsWithTotal = [
      ...sortedSummaryRows,
      { id: null, name: 'Total', ...entityViewTotal },
    ];
  }

  return (
    <StyledContainer data-test="reportDetails">
      <TabsGroup value={activeMenuItem} onValueChange={(id) => selectActiveMenuItem(id)}>
        <NavTabsList>
          <NavTabTrigger value={ActiveMenuItemEnum.componentTable} data-test="componentTableTab">
            {report.componentType} View
          </NavTabTrigger>
          <NavTabTrigger value={ActiveMenuItemEnum.dateTimeTable} data-test="dateTimeTab">
            Date-Time View
          </NavTabTrigger>
        </NavTabsList>
        <TabsContent value={ActiveMenuItemEnum.componentTable}>
          <Table data-test="componentTable">
            <TableHeader
              columns={entityViewSummaryData?.columns || []}
              sortedBy={sortedBy}
              setSortedBy={setSortedBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
            <TableRows
              rows={sortedSummaryRowsWithTotal}
              columns={entityViewSummaryData?.columns || []}
            />
          </Table>
        </TabsContent>
        <TabsContent value={ActiveMenuItemEnum.dateTimeTable}>
          <Table data-test="dateTimeTable">
            <TableHeader
              columns={dateTimeTableData?.columns || []}
              sortedBy={sortedBy}
              setSortedBy={setSortedBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
            <TableRows rows={sortedDateTimeRows} columns={dateTimeTableData?.columns || []} />
          </Table>
        </TabsContent>
      </TabsGroup>
    </StyledContainer>
  );
};

export default ReportDetails;
